import React, { useEffect, useMemo } from 'react';
import { BackspaceRegularIcon, CommandRegularIcon, ShiftRegularIcon } from 'src/monet/icons';
import { ContextMenuButton } from './components/context-menu-button';
import { useEditorStore } from './editor-store';
import { Block } from './types';
import { useCreateBlockActions } from './utils/create-block-actions.hook';
import { useClipboardActions } from './utils/clipboard-actions.hook';
import classNames from 'classnames';
import { shallow } from 'zustand/shallow';
import { useClipboardStore } from './utils/clipboard.store';

type Props = {
  block: Block;
};

export const getRenderShortcut =
  (letter: string, shift: boolean = false) =>
  (isMac: boolean) =>
    (
      <div className='flex items-center gap-1 w-10 justify-end'>
        {isMac ? (
          <>
            {shift && <ShiftRegularIcon className='w-4 h-4 shrink-0' />}
            <CommandRegularIcon className='w-4 h-4 shrink-0' />
          </>
        ) : (
          <>
            <span className='text-neue-journey-medium text-neue-journey-fg-50'>Ctrl</span>
            {shift && <ShiftRegularIcon className='w-4 h-4 shrink-0' />}
          </>
        )}
        <span className='text-neue-journey-medium text-neue-journey-fg-50'>{letter}</span>
      </div>
    );

export const BlockContextMenuContent = ({ block }: Props) => {
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const clearContextMenuState = useEditorStore((state) => state.clearContextMenuState);
  const clearSelectedBlock = useEditorStore((state) => state.clearSelectedBlock);
  const getSelectedBlock = useEditorStore((state) => state.getSelectedBlock);

  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);

  const getBlockById = useEditorStore((state) => state.getBlockById);

  const undoable = useEditorStore((state) => state.undoStack.length > 0, shallow);
  const redoable = useEditorStore((state) => state.redoStack.length > 0, shallow);

  const contextMenuState = useEditorStore((state) => state.contextMenuState);

  const removeBlockActionsForBlockId = useClipboardStore((state) => state.removeBlockActionsForBlockId);
  const setBlockActionsForBlockId = useClipboardStore((state) => state.setBlockActionsForBlockId);
  const actionBlockedBlockIds = useClipboardStore((state) => state.blockActionsForBlockId, shallow);

  const enablePasteForBlock = useClipboardStore((state) => state.enablePasteForBlock, shallow);
  const { cutBlockByBlockId, copyBlockByBlockId } = useClipboardActions();
  const { duplicateBlock, pasteBlock } = useCreateBlockActions();

  const selectedBlock = getSelectedBlock();

  // temporarily disable copy/cut/paste/duplicate for mutual-action-plan
  useEffect(() => {
    const blockIdToAction =
      contextMenuState?.entity.type === 'block' ? contextMenuState.entity.blockId : selectedBlock?.id;
    const block = blockIdToAction ? getBlockById(blockIdToAction) : undefined;
    if (block && block.content.type === 'mutual-action-plan') {
      setBlockActionsForBlockId(block.id);
    }

    return () => {
      if (block) {
        removeBlockActionsForBlockId(block.id);
      }
    };
  }, [contextMenuState, selectedBlock?.id]);

  const onDeleteBlock = () => {
    dispatchUserEditorAction({
      type: 'delete-block',
      id: block.id,
    });
    clearSelectedBlock();
    clearContextMenuState();
  };

  const onCopyBlock = async () => {
    if (contextMenuState && contextMenuState.entity.type === 'block') {
      await copyBlockByBlockId(contextMenuState.entity.blockId);
    } else if (selectedBlock) {
      await copyBlockByBlockId(selectedBlock.id);
    }
    clearContextMenuState();
  };

  const onPasteBlock = () => {
    if (contextMenuState && contextMenuState.entity.type === 'block') {
      pasteBlock(contextMenuState.entity.blockId);
    } else if (selectedBlock) {
      pasteBlock(selectedBlock.id);
    }
    clearContextMenuState();
  };

  const onCutBlock = async () => {
    if (contextMenuState && contextMenuState.entity.type === 'block') {
      await cutBlockByBlockId(contextMenuState.entity.blockId);
    } else if (selectedBlock) {
      await cutBlockByBlockId(selectedBlock.id);
    }
    clearContextMenuState();
  };

  const onDuplicateBlock = () => {
    if (contextMenuState && contextMenuState.entity.type === 'block') {
      duplicateBlock(contextMenuState.entity.blockId);
    } else if (selectedBlock) {
      duplicateBlock(selectedBlock.id);
    }
    clearContextMenuState();
  };

  const onUndoAction = () => {
    undo();
    clearContextMenuState();
  };

  const onRedoAction = () => {
    redo();
    clearContextMenuState();
  };

  const BlockContextMenuOptions = useMemo(
    () => [
      {
        label: 'Duplicate',
        renderShortcut: getRenderShortcut('D'),
        onClick: onDuplicateBlock,
        disable:
          contextMenuState?.entity.type === 'block' && actionBlockedBlockIds.includes(contextMenuState.entity.blockId),
      },
      {
        label: 'Cut',
        renderShortcut: getRenderShortcut('X'),
        onClick: onCutBlock,
        disable:
          contextMenuState?.entity.type === 'block' && actionBlockedBlockIds.includes(contextMenuState.entity.blockId),
      },
      {
        label: 'Copy',
        renderShortcut: getRenderShortcut('C'),
        onClick: onCopyBlock,
        disable:
          contextMenuState?.entity.type === 'block' && actionBlockedBlockIds.includes(contextMenuState.entity.blockId),
      },
      {
        label: 'Paste',
        renderShortcut: getRenderShortcut('V'),
        onClick: onPasteBlock,
        disable: !enablePasteForBlock,
      },
      {
        label: 'Delete',
        renderShortcut: () => <BackspaceRegularIcon className='w-4 h-4' />,
        onClick: onDeleteBlock,
      },
    ],
    [actionBlockedBlockIds, contextMenuState, enablePasteForBlock]
  );

  const BasicContextMenuOptions = useMemo(
    () => [
      {
        label: 'Undo',
        renderShortcut: getRenderShortcut('Z'),
        onClick: onUndoAction,
        disable: !undoable,
      },
      {
        label: 'Redo',
        renderShortcut: getRenderShortcut('Z', true),
        onClick: onRedoAction,
        disable: !redoable,
      },
    ],
    [undoable, redoable]
  );

  const isOSMac = navigator.userAgent.includes('Mac');

  return (
    <>
      {BlockContextMenuOptions.map((option) => {
        return (
          <ContextMenuButton
            onClick={option.onClick}
            className={classNames('justify-between', {
              'opacity-50 pointer-events-none': option.disable,
            })}
          >
            <>
              <div className='flex items-center gap-2'>
                <span className='text-neue-journey-medium'>{option.label}</span>
              </div>
              {option.renderShortcut(isOSMac)}
            </>
          </ContextMenuButton>
        );
      })}
      <div className='h-px w-full bg-neue-journey-fg my-2 opacity-[0.06]'></div>
      {BasicContextMenuOptions.map((option) => {
        return (
          <ContextMenuButton
            onClick={option.onClick}
            className={classNames('justify-between', {
              'opacity-50 pointer-events-none': option.disable,
            })}
          >
            <>
              <div className='flex items-center gap-2'>
                <span className='text-neue-journey-medium'>{option.label}</span>
              </div>
              {option.renderShortcut(isOSMac)}
            </>
          </ContextMenuButton>
        );
      })}
    </>
  );
};
